// Shared helpers reused by every "IBs" report tab (Summarized Report, Full
// Report, and any future report tab), so the parsing/formatting/sorting/
// grouping logic - and the "All / individual" selector behaviour - only
// lives in one place.

export const DATE_RANGE_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "This week", value: "This week" },
  { label: "This month", value: "This month" },
  { label: "This year", value: "This year" },
  { label: "Custom", value: "Custom" },
];

export const TRANSACTION_TYPE_OPTIONS = [
  { label: "Deposit", value: "DEPOSIT" },
  { label: "Withdraw", value: "WITHDRAW" },
  { label: "Payout", value: "PAYOUT" },
];

// Shared pagination defaults for every "IBs" report table (Summarized
// Report and Full Report): a minimum/default page size of 10 rows, with a
// selector to bump it up to 25/50/100 (or down to 5) when needed.
export const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];
export const DEFAULT_ROWS_PER_PAGE = 10;

export const fmt = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "-";

  const [integer, decimal] = Number(n).toFixed(2).split(".");

  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, " ") + "." + decimal;
};

// Robust date parsing for "YYYY/MM/DD HH:mm:ss" or "YYYY-MM-DD HH:mm:ss".
export const parseDateToMs = (value) => {
  if (!value) return 0;
  const str = String(value).trim();

  const [datePart, timePart = "00:00:00"] = str.split(" ");
  const sep = datePart.includes("/") ? "/" : "-";
  const [yy, mm, dd] = datePart.split(sep).map((x) => Number(x));
  const [hh = 0, mi = 0, ss = 0] = timePart.split(":").map((x) => Number(x));

  if (!yy || !mm || !dd) return 0;
  const date = new Date(yy, mm - 1, dd, hh || 0, mi || 0, ss || 0);
  const ms = date.getTime();
  return Number.isFinite(ms) ? ms : 0;
};

// Groups rows by their `hierarchy_id` ("8", "8-2", "9", ...): the part
// before the dash identifies the parent row, everything else becomes an
// (initially collapsed) child row underneath it. Uses a Map (instead of a
// plain object) so numeric-looking keys never get silently reordered.
export const groupReportRowsByHierarchy = (rows) => {
  const map = new Map();

  rows.forEach((row) => {
    if (!row?.hierarchy_id) return;

    const [parentId] = String(row.hierarchy_id).split("-");

    if (!map.has(parentId)) {
      map.set(parentId, { parentId, parent: null, children: [] });
    }

    const group = map.get(parentId);

    if (!group.parent) {
      group.parent = row;
    } else {
      group.children.push(row);
    }
  });

  return Array.from(map.values());
};

// The "Summarized Report" tab shows one row per tree/IB (already-aggregated
// totals), not the full parent/children breakdown - so it only needs the
// "parent" row of each hierarchy group. The per-transaction breakdown that
// used to live in the (now removed) expandable child rows is still fully
// available, transaction by transaction, in the "Full Report" tab.
export const getSummaryRowsFromReportRows = (rows) =>
  groupReportRowsByHierarchy(rows)
    .map((group) => group.parent)
    .filter(Boolean);

// Normalizes a raw API report entry (as returned by both /ib-report and
// /ib-report-full) into the row shape every "IBs" report table works with.
export const buildReportRow = (report, index) => {
  let formattedDate = "";

  try {
    let rawDate = report?.date ?? "";
    if (rawDate.includes("T")) {
      rawDate = rawDate.replace("T", " ");
    }

    const raw = rawDate.split(" ")[0];
    const time = rawDate.split(" ")[1];
    formattedDate = `${raw.replace(/-/g, "/")} ${time ?? ""}`.trim();
  } catch (e) {
    console.error(report?.date);
  }

  return {
    id: index,
    hierarchy_id: String(report.hierarchy_id),
    lastTxnDate: report.lastTxnDate,
    date: formattedDate,
    ib_id: report.ib_id,
    ib_email: report.ib_email,
    name: report.name,
    deposit: parseFloat(Number(report.deposit ?? 0).toFixed(2)),
    withdraw: parseFloat(Number(report.withdraw ?? 0).toFixed(2)),
    payout: parseFloat(Number(report.payout ?? 0).toFixed(2)),
    wallet: parseFloat(Number(report.wallet ?? 0).toFixed(2)),
    currency: report.currency,
  };
};

// Mirrors the original "All / individual" toggle logic used by the IB
// multi-selector: keeps "All" and the individual list in sync no matter
// which one the user just changed.
export const computeNextIbSelection = (selectedValues, currentSelection, allOptionValues) => {
  const isAllSelected = selectedValues.includes("All");
  const hadAllBefore = currentSelection.includes("All");

  if (isAllSelected && !hadAllBefore) {
    return allOptionValues;
  }

  if (!isAllSelected && hadAllBefore) {
    return [];
  }

  if (hadAllBefore && selectedValues.length < allOptionValues.length) {
    return selectedValues.filter((val) => val !== "All");
  }

  const selectedWithoutAll = selectedValues.filter((val) => val !== "All");
  const allExceptAll = allOptionValues.filter((val) => val !== "All");

  const selectedAllManually =
    selectedWithoutAll.length === allExceptAll.length &&
    allExceptAll.every((val) => selectedWithoutAll.includes(val));

  if (selectedAllManually) {
    return allOptionValues;
  }

  return selectedValues;
};

// Turns a selector value (which may include the synthetic "All" option)
// into the list of real email addresses an API call should use.
export const resolveSelectedEmails = (selection, individualEmails) => {
  if (!Array.isArray(selection) || selection.length === 0) return [];
  if (selection.includes("All")) return individualEmails;
  return selection.filter((value) => value !== "All");
};

// Generic version of the above: works for any list of real values (emails,
// tree ids as strings, etc.), not just emails.
export const resolveSelectedValues = resolveSelectedEmails;

/* =========================
   Full Report (POST /ib-report-full) specific helpers
   -----------------------------------------------------
   This endpoint returns flat, transaction-level rows (one row per
   deposit/withdraw/payout), not the hierarchical/grouped rows the
   Summarized Report (/ib-report) uses - so it gets its own row builder,
   date parser and sorter instead of reusing buildReportRow/parseDateToMs.
========================= */

// The Full Report API returns dates as "DD/MM/YYYY HH:mm:ss", e.g.
// "01/07/2026 05:22:14".
export const parseFullReportDateToMs = (value) => {
  if (!value) return 0;
  const str = String(value).trim();

  const [datePart, timePart = "00:00:00"] = str.split(" ");
  const [dd, mm, yy] = datePart.split("/").map((x) => Number(x));
  const [hh = 0, mi = 0, ss = 0] = timePart.split(":").map((x) => Number(x));

  if (!yy || !mm || !dd) return 0;
  const date = new Date(yy, mm - 1, dd, hh || 0, mi || 0, ss || 0);
  const ms = date.getTime();
  return Number.isFinite(ms) ? ms : 0;
};

// Normalizes a raw /ib-report-full report entry into the row shape
// FullReportTab's table works with.
export const buildFullReportRow = (report, index) => ({
  id: index,
  date: report?.date ?? "",
  ib_id: report?.ib_id,
  name: report?.ib_name,
  ib_email: report?.ib_email,
  transactionType: report?.transaction_type,
  amount: parseFloat(Number(report?.amount ?? 0).toFixed(2)),
  volume: parseFloat(Number(report?.volume ?? 0).toFixed(2)),
  currency: report?.currency,
  platform: report?.platform,
  account: report?.account,
  ticket: report?.ticket,
  comments: report?.comments,
});